import { t } from "@lingui/macro";
import {
  CaretDown,
  ChatTeardropText,
  CircleNotch,
  Exam,
  MagicWand,
  PenNib,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useState } from "react";

import { toast } from "../hooks/use-toast";
import { changeTone, fixGrammar, improveWriting } from "../services/openai/client";
import { useUser } from "@/client/services/user";
import { Mood, Action } from "@reactive-resume/dto";
import { isTemporaryResume } from "@/client/constants/resume";
import { useResumeStore } from "@/client/stores/resume";

type Props = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

export const AiActions = ({ value, onChange, className }: Props) => {
  const [loading, setLoading] = useState<Action | false>(false);
  const userId = useResumeStore((state) => state.resume.userId);
  const { user } = useUser(!isTemporaryResume(userId));
  const emailVerified = user?.emailVerified ?? false;

  const onClick = async (action: Action, mood?: Mood) => {
    if (!emailVerified) {
      toast({
        variant: "error",
        title: t`Access Denied`,
        description: t`Please verify your email to use AI features`,
      });
      return;
    }

    try {
      setLoading(action);

      let result = value;

      if (action === "improve") result = await improveWriting(value);
      if (action === "fix") result = await fixGrammar(value);
      if (action === "tone" && mood) result = await changeTone(value, mood);

      onChange(result);
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={cn(
        "relative mt-4 rounded bg-secondary-accent/50 p-3 outline outline-secondary-accent",
        "flex flex-wrap items-center justify-center gap-2",
        className,
      )}
    >
      <Tooltip content={!emailVerified ? t`Please verify your email to use AI features` : ""}>
        <div className="absolute right-0 top-0 z-10">
          <Badge
            outline
            variant="primary"
            className="bg-background"
          >
            <MagicWand size={14} />
          </Badge>
        </div>
      </Tooltip>

      <Tooltip content={!emailVerified ? t`Please verify your email to use AI features` : t`Improve Writing`}>
        <Button size="sm" variant="outline" disabled={!!loading} onClick={() => onClick("improve")}>
          {loading === "improve" ? <CircleNotch className="animate-spin" /> : <PenNib />}
          <span className="ml-2 text-xs">{t`Improve Writing`}</span>
        </Button>
      </Tooltip>

      <Tooltip content={!emailVerified ? t`Please verify your email to use AI features` : t`Fix Spelling & Grammar`}>
        <Button size="sm" variant="outline" disabled={!!loading} onClick={() => onClick("fix")}>
          {loading === "fix" ? <CircleNotch className="animate-spin" /> : <Exam />}
          <span className="ml-2 text-xs">{t`Fix Spelling & Grammar`}</span>
        </Button>
      </Tooltip>

      <DropdownMenu>
        <Tooltip content={!emailVerified ? t`Please verify your email to use AI features` : t`Change Tone`}>
        <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" disabled={!!loading}>
              {loading === "tone" ? <CircleNotch className="animate-spin" /> : <ChatTeardropText />}
              <span className="mx-2 text-xs">{t`Change Tone`}</span>
              <CaretDown />
            </Button>
        </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => onClick("tone", "casual")}>
            <span role="img" aria-label={t`Casual`}>
              🙂
            </span>
            <span className="ml-2">{t`Casual`}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClick("tone", "professional")}>
            <span role="img" aria-label={t`Professional`}>
              💼
            </span>
            <span className="ml-2">{t`Professional`}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClick("tone", "confident")}>
            <span role="img" aria-label={t`Confident`}>
              😎
            </span>
            <span className="ml-2">{t`Confident`}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClick("tone", "friendly")}>
            <span role="img" aria-label={t`Friendly`}>
              😊
            </span>
            <span className="ml-2">{t`Friendly`}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
